import { useContext, useEffect, useState } from 'react'

import { SignalRStateContext } from '@/lib/signalR/store/context'
import type { Payload } from '@/lib/signalR/store/types'

export const EVENT_TYPES = {
  SubjectAccepted: 'subject.accepted',
  SubjectRejected: 'subject.rejected',
  SubjectPass: 'subject.pass',
  SubjectFail: 'subject.fail',
  SubjectCancelled: 'subject.cancelled',
  SubjectReinstated: 'subject.reinstated',
  SubjectReopened: 'subject.reopened',
  ReportUpdated: 'report.updated',
  ReportGenerated: 'subject.report.generated',
  SubjectResultsEvaluated: 'subject.results.evaluated',
  TouchpointCreated: 'touchpoint.created',
  SubjectOutcomeOverridden: 'subject.outcome.overridden',
  SubjectOutcomeUpdated: 'subject.outcome.updated',
  SubjectUpdated: 'subject.updated',
  SubjectSectionReset: 'subject.section.reset',
  SubjectSupportContactCreated: 'subject.support.contact.created',
  SubjectSupportContactUpdated: 'subject.support.contact.updated',
}

const isEvent = (event: unknown): event is Payload =>
  typeof event === 'object' && (event as Payload)?.SubjectId !== undefined

export type ShouldHandleEvent = (event: Payload) => boolean

export type EventHandler = (event: Payload) => void

interface UseSubscribeSignalREventsArgs {
  shouldHandleEvent: ShouldHandleEvent
  eventHandler: EventHandler
}

export const useSignalREventHandler = ({
  shouldHandleEvent,
  eventHandler,
}: UseSubscribeSignalREventsArgs) => {
  const {
    state: { event },
  } = useContext(SignalRStateContext)

  const [lastEventId, setLastEventId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!isEvent(event)) {
      return
    }

    if (event.Id === lastEventId) {
      console.debug('Event has already been handled.')

      return
    }

    if (!shouldHandleEvent(event)) {
      return
    }

    console.debug(`Handling SignalR event. Event ID: ${event.Id}`)

    setLastEventId(event.Id)
    eventHandler(event)
  }, [lastEventId, event, shouldHandleEvent, eventHandler])
}
